"use client"

// /brand-engineering — client sections (V3-MASTER-SPEC §2.2).
// Register map (方向 C 雙域收斂): narrative blocks live on PAPER; the worked
// examples, behind-the-scene panel and embedded instrument live in DARK
// instrument rooms. 「湧現」 is legal on this page (hard rule ②).

import { DiagnosticInstrument } from "@/components/proto/diagnostic-instrument"
import { CtaBlock, InstrumentRoom } from "@/components/v3/site-chrome"
import { Eyebrow, FadeUp, ZH_SERIF, dark, paper } from "@/components/v3/primitives"
import { framework, methodPage, pullQuotes } from "@/lib/proto/content"
import { diagnosticExamples, sampleNote } from "@/lib/proto/diagnostic-examples"

// ── (1) 定義 v1.0 — LOCKED, versioned ────────────────────────────────────────

export function DefinitionSection() {
  return (
    <section aria-label="定義" className="relative mx-auto max-w-6xl px-6 pb-24 pt-28 md:px-10 md:pb-32 md:pt-36">
      <FadeUp>
        <Eyebrow zh="方法" en="The method" />
        <div className="mt-8 flex flex-wrap items-baseline gap-x-5 gap-y-2">
          <h1
            lang="zh-Hant"
            className="text-5xl leading-[1.15] md:text-[4.6rem]"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 640", color: paper.ink }}
          >
            {framework.name.zh}
          </h1>
          <span
            className="text-lg italic tracking-wide md:text-xl"
            style={{ fontFamily: "var(--proto-fraunces)", color: paper.accent }}
          >
            {framework.name.en}
          </span>
          {/* Version badge — the definition is versioned like software (§2.2) */}
          <span
            className="rounded-full px-3 py-1 text-[11px] tracking-[0.14em]"
            style={{
              fontFamily: "var(--proto-mono)",
              border: `1px solid ${paper.hairline}`,
              color: paper.inkFaint,
            }}
            title="定義依市場回饋版本化演進"
          >
            定義 {methodPage.definition.version} · 2026-07
          </span>
        </div>
      </FadeUp>

      <FadeUp delay={0.08}>
        <p
          lang="zh-Hant"
          className="mt-14 max-w-4xl text-balance text-2xl leading-[1.75] md:text-[2.1rem] md:leading-[1.7]"
          style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 500", color: paper.ink }}
        >
          {methodPage.definition.zh}
        </p>
        <p
          className="mt-8 max-w-3xl pl-5 text-[13px] leading-relaxed"
          style={{
            fontFamily: "var(--proto-hanken)",
            borderLeft: `1px solid ${paper.hairline}`,
            color: paper.inkFaint,
          }}
        >
          {methodPage.definition.en}
        </p>
      </FadeUp>
    </section>
  )
}

// ── (2) 為什麼品牌是系統 — 羽毛成鳥＋白話錨定 ────────────────────────────────

export function WhySystemSection() {
  const anchor = methodPage.whySystem.anchor
  return (
    <section
      aria-label="為什麼品牌是系統"
      className="relative py-24 md:py-36"
      style={{ background: paper.bgDeep, borderTop: `1px solid ${paper.hairlineSoft}`, borderBottom: `1px solid ${paper.hairlineSoft}` }}
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <FadeUp>
          <Eyebrow zh="為什麼品牌是系統" en="Why a brand is a system" />
        </FadeUp>
        <div className="mt-12 max-w-4xl space-y-9">
          {methodPage.whySystem.zh.map((para, i) => (
            <FadeUp key={i} delay={i * 0.06}>
              <p
                lang="zh-Hant"
                className="text-balance text-xl leading-[1.85] md:text-[1.55rem] md:leading-[1.8]"
                style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 450", color: paper.ink }}
              >
                {para.includes(anchor.zh) ? (
                  <>
                    {para.split(anchor.zh)[0]}
                    <span style={{ color: paper.accent, fontVariationSettings: "'wght' 620" }}>
                      {anchor.zh}
                    </span>
                    {para.split(anchor.zh)[1]}
                  </>
                ) : (
                  para
                )}
              </p>
            </FadeUp>
          ))}
        </div>
        <FadeUp delay={0.2}>
          <div
            className="mt-12 max-w-3xl space-y-3 pl-5"
            style={{ borderLeft: `1px solid ${paper.hairline}` }}
          >
            {methodPage.whySystem.en.map((para, i) => (
              <p
                key={i}
                className="text-[13px] leading-relaxed"
                style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
              >
                {para}
              </p>
            ))}
          </div>
        </FadeUp>
      </div>
    </section>
  )
}

// ── (3) 金句滿版 — LOCKED pull quote (deck §10 #6) ───────────────────────────

export function PullQuoteSection() {
  const quote = pullQuotes.find((q) => q.zh.startsWith("品牌不是元素清單"))
  if (!quote) return null
  return (
    <section aria-label="金句" className="relative overflow-hidden py-32 md:py-48">
      <FadeUp className="relative px-6 md:px-10">
        <blockquote className="mx-auto max-w-none text-center">
          <p
            lang="zh-Hant"
            className="text-balance text-4xl leading-[1.45] md:text-[4rem] md:leading-[1.4]"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 640", color: paper.ink }}
          >
            品牌不是元素清單，
            <br className="hidden md:block" />
            而是一個能讓信任<span style={{ color: paper.accent }}>湧現</span>的場。
          </p>
          <p
            className="mx-auto mt-8 max-w-2xl text-[15px] italic"
            style={{ fontFamily: "var(--proto-fraunces)", color: paper.inkFaint }}
          >
            {quote.en}
          </p>
        </blockquote>
      </FadeUp>
    </section>
  )
}

// ── (4) 四模組 ────────────────────────────────────────────────────────────────

export function ModulesSection() {
  return (
    <section
      aria-label="四個模組"
      className="relative py-24 md:py-36"
      style={{ background: paper.bgDeep, borderTop: `1px solid ${paper.hairlineSoft}`, borderBottom: `1px solid ${paper.hairlineSoft}` }}
    >
      <div className="mx-auto max-w-6xl px-6 md:px-10">
        <FadeUp>
          <Eyebrow zh="怎麼運作" en="How it works" />
          <h2
            lang="zh-Hant"
            className="mt-6 text-3xl leading-tight md:text-5xl"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 580", color: paper.ink }}
          >
            四個模組
          </h2>
        </FadeUp>

        <div
          className="mt-16 grid grid-cols-1 gap-px overflow-hidden rounded-2xl md:grid-cols-2"
          style={{ background: paper.hairline, border: `1px solid ${paper.hairline}` }}
        >
          {framework.modules.map((m, i) => (
            <FadeUp key={m.id} delay={i * 0.05}>
              <article className="flex h-full flex-col p-8 md:p-10" style={{ background: paper.bg }}>
                <span className="text-sm" style={{ fontFamily: "var(--proto-mono)", color: paper.inkFaint }}>
                  0{i + 1}
                </span>
                <div className="mt-4 flex flex-wrap items-baseline gap-x-3">
                  <h3
                    lang="zh-Hant"
                    className="text-2xl md:text-[1.8rem]"
                    style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 600", color: paper.ink }}
                  >
                    {m.name.zh}
                  </h3>
                  <span
                    className="text-[13px] italic"
                    style={{ fontFamily: "var(--proto-fraunces)", color: paper.accent }}
                  >
                    {m.name.en}
                  </span>
                </div>
                <p
                  lang="zh-Hant"
                  className="mt-5 text-[15px] leading-[1.9]"
                  style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
                >
                  {m.line.zh}
                </p>
                <p
                  className="mt-4 text-[12.5px] leading-relaxed"
                  style={{ fontFamily: "var(--proto-hanken)", color: paper.inkFaint }}
                >
                  {m.line.en}
                </p>
              </article>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  )
}

// ── (5) Builder/Maker × AI-native — judgment over automation ─────────────────

export function AiNativeSection() {
  const em = methodPage.aiNative.emphasis
  return (
    <section aria-label="AI-native 工作方式" className="relative mx-auto max-w-6xl px-6 py-24 md:px-10 md:py-36">
      <FadeUp>
        <Eyebrow zh="動手做的策略者" en="A strategist who builds" />
      </FadeUp>
      <div className="mt-12 grid grid-cols-1 gap-14 lg:grid-cols-[minmax(0,1.35fr)_minmax(0,1fr)]">
        <div className="max-w-3xl space-y-8">
          {methodPage.aiNative.zh.map((para, i) => (
            <FadeUp key={i} delay={i * 0.06}>
              <p
                lang="zh-Hant"
                className="text-lg leading-[1.95] md:text-xl md:leading-[1.9]"
                style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
              >
                {para.includes(em.zh) ? (
                  <>
                    {para.split(em.zh)[0]}
                    <span style={{ color: paper.accent, fontWeight: 600 }}>{em.zh}</span>
                    {para.split(em.zh)[1]}
                  </>
                ) : (
                  para
                )}
              </p>
            </FadeUp>
          ))}
          <FadeUp delay={0.2}>
            <p
              className="pl-5 text-[13px] leading-relaxed"
              style={{
                fontFamily: "var(--proto-hanken)",
                borderLeft: `1px solid ${paper.hairline}`,
                color: paper.inkFaint,
              }}
            >
              {methodPage.aiNative.en.join(" ")}
            </p>
          </FadeUp>
        </div>

        {/* Behind-the-scene panel (§2.2) — instrument register: this IS the
            machine room. Build log links land with /now (W3–4). */}
        <FadeUp delay={0.1}>
          <InstrumentRoom className="p-7 md:p-8">
            <p
              className="text-[11px] tracking-[0.24em]"
              style={{ fontFamily: "var(--proto-mono)", color: dark.accent }}
            >
              BEHIND THE SCENE
            </p>
            <ul className="mt-6 space-y-4">
              {[
                { k: "建造方式", v: "人類判斷 × 多代理 AI 系統" },
                { k: "本頁文案", v: "copy deck v3 逐字，零 AI 湊字" },
                { k: "品質閘", v: "CI 機器閘 → agent 閘 → 人類閘" },
                { k: "Build log", v: "整理中，上線後在這裡" },
              ].map((row) => (
                <li key={row.k} className="grid grid-cols-[6rem_minmax(0,1fr)] gap-3">
                  <span
                    lang="zh-Hant"
                    className="text-[12px]"
                    style={{ fontFamily: "var(--proto-mono)", color: dark.faint }}
                  >
                    {row.k}
                  </span>
                  <span
                    lang="zh-Hant"
                    className="text-[13.5px] leading-relaxed"
                    style={{ fontFamily: "var(--proto-hanken)", color: dark.text }}
                  >
                    {row.v}
                  </span>
                </li>
              ))}
            </ul>
          </InstrumentRoom>
        </FadeUp>
      </div>
    </section>
  )
}

// ── (6) 診斷範例 — 證據衝刺 #3（示範樣本，非客戶資料）─────────────────────────
// Instrument register: worked examples render as terminal readouts.

export function ExamplesSection() {
  return (
    <section aria-label="診斷範例" className="relative mx-auto max-w-6xl px-6 py-24 md:px-10 md:py-36">
      <FadeUp>
        <Eyebrow zh="輸出長什麼樣" en="What the output looks like" />
        <h2
          lang="zh-Hant"
          className="mt-6 text-3xl leading-tight md:text-5xl"
          style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 580", color: paper.ink }}
        >
          三個示範診斷
        </h2>
        <p
          lang="zh-Hant"
          className="mt-6 max-w-2xl text-[14px] leading-[1.9]"
          style={{ fontFamily: "var(--proto-hanken)", color: paper.inkSoft }}
        >
          {sampleNote.zh}
        </p>
      </FadeUp>

      <div className="mt-14 space-y-6">
        {diagnosticExamples.map((ex, i) => (
          <FadeUp key={ex.id} delay={i * 0.05}>
            <InstrumentRoom className="p-7 md:p-10">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3
                  lang="zh-Hant"
                  className="text-lg md:text-xl"
                  style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 580", color: dark.text }}
                >
                  {ex.label.zh}
                </h3>
                <span
                  className="rounded-full px-3 py-1 text-[10.5px] tracking-[0.18em]"
                  style={{
                    fontFamily: "var(--proto-mono)",
                    border: `1px solid ${dark.border}`,
                    color: dark.faint,
                  }}
                >
                  示範樣本 · SAMPLE
                </span>
              </div>

              <blockquote
                lang="zh-Hant"
                className="mt-6 pl-5 text-[14.5px] leading-[1.9]"
                style={{
                  fontFamily: "var(--proto-hanken)",
                  borderLeft: `2px solid ${dark.border}`,
                  color: dark.soft,
                }}
              >
                「{ex.input}」
              </blockquote>

              <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-3">
                {(
                  [
                    { key: "clarity", zh: "清晰度" },
                    { key: "differentiation", zh: "差異化" },
                    { key: "evidence", zh: "證據力" },
                  ] as const
                ).map((dim) => {
                  const v = ex.scores[dim.key]
                  const tone = v < 35 ? dark.warn : v < 60 ? dark.soft : dark.accent
                  return (
                    <div key={dim.key}>
                      <div className="flex items-baseline justify-between">
                        <span
                          lang="zh-Hant"
                          className="text-[12px] tracking-[0.14em]"
                          style={{ fontFamily: "var(--proto-mono)", color: dark.soft }}
                        >
                          {dim.zh}
                        </span>
                        <span className="text-lg" style={{ fontFamily: "var(--proto-mono)", color: tone }}>
                          {v}
                        </span>
                      </div>
                      <div className="mt-2 h-1 overflow-hidden rounded-full" style={{ background: dark.border }}>
                        <div
                          className="h-full rounded-full"
                          style={{ width: `${v}%`, background: tone }}
                          aria-hidden
                        />
                      </div>
                    </div>
                  )
                })}
              </div>

              <p
                lang="zh-Hant"
                className="mt-8 text-[14.5px] leading-[1.95]"
                style={{ fontFamily: "var(--proto-hanken)", color: dark.soft }}
              >
                {ex.findings.zh}
              </p>

              <p
                lang="zh-Hant"
                className="mt-6 text-balance text-lg leading-[1.7] md:text-xl"
                style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 560", color: dark.accent }}
              >
                {ex.tensionLine.zh}
              </p>
              <p
                className="mt-2 text-[12.5px] italic"
                style={{ fontFamily: "var(--proto-fraunces)", color: dark.faint }}
              >
                {ex.tensionLine.en}
              </p>
            </InstrumentRoom>
          </FadeUp>
        ))}
      </div>
    </section>
  )
}

// ── (7) 診斷器內嵌＋(8) CTA ──────────────────────────────────────────────────

export function DiagnosticSection() {
  return (
    <section
      id="diagnostic"
      aria-label="90 秒診斷"
      className="relative mx-auto max-w-6xl scroll-mt-24 px-6 py-24 md:px-10 md:py-36"
    >
      <FadeUp>
        <div className="mb-12 text-center">
          <Eyebrow zh="換你" en="Your turn" />
          <h2
            lang="zh-Hant"
            className="mx-auto mt-6 max-w-3xl text-balance text-3xl leading-[1.4] md:text-5xl"
            style={{ fontFamily: ZH_SERIF, fontVariationSettings: "'wght' 580", color: paper.ink }}
          >
            90 秒，看你的定位卡在哪
          </h2>
        </div>
      </FadeUp>
      <FadeUp delay={0.08}>
        <InstrumentRoom className="p-5 md:p-10">
          <DiagnosticInstrument accent={dark.accent} />
        </InstrumentRoom>
      </FadeUp>
    </section>
  )
}

export function MethodCta() {
  // B6 (v3)：closing 去重複＋補 en
  return <CtaBlock sub={{ zh: methodPage.closing.zh, en: methodPage.closing.en }} />
}
